import { Skull, Zap, AlertTriangle } from "lucide-react";
import { motion } from "framer-motion";
import { nationalFlag } from "@/lib/flags";
import { BentoTileGrid } from "@/components/onui/BentoMotion";
import { isPlayoffFieldFinal } from "@/lib/playoffField";
import { TeamSwatch } from "@/components/dashboard/TeamSwatch";

// iter87.420 — Non-country teams (club/franchise templates) have no
// real flag emoji to show, so `nationalFlag()` returns null and the
// grid used to fall back to a plain white 🏳️ placeholder. Map each
// API-driven template to the `TeamSwatch` sport key so those teams
// get the same brand-colored initials circle used everywhere else
// (standings, series pickers, etc.) instead of the generic flag.
function sportForTemplate(template) {
    if (template === "mlb_playoffs_api") return "mlb";
    if (template === "nba_playoffs_api") return "nba";
    if (template === "nwsl_playoffs_api") return "nwsl";
    if (["nfl_season_api", "nfl_boost_api", "nfl_playoffs_api"].includes(template)) return "nfl";
    if (["premier_league_api", "epl_boost_api"].includes(template)) return "epl";
    if (["la_liga_api", "la_liga_boost_api"].includes(template)) return "laliga";
    if (["champions_league_api", "club_season_api", "world_cup_api", "world_cup_ko_api"].includes(template)) return "soccer";
    return null;
}

/**
 * iter87.14 — Bento-styled team grid (matches BentoHome / TodoChips /
 * Leaderboard rows). Replaces the legacy brutal-card + sharp-border
 * `border-2 rounded-md` cards. Each team is a `rounded-lg` tile with a
 * hairline `white/[0.08]` border; power picks get a soft volt glow +
 * ribbon; eliminated teams dim + apply a `line-through` cross on the
 * name to visualise the loss without dropping legibility.
 */
const STAGE_LABELS = {
    group: "Group Stage", r64: "Round of 64", r32: "Round of 32", r16: "Round of 16",
    qf: "Quarterfinals", sf: "Semifinals", final: "Final", champion: "Champion",
};

export default function MyTeamsGrid({ teams, breakdown = [], compact = false, isSeason = false, isNfl = false, tournament = null }) {
    if (!teams || teams.length === 0) return null;
    const breakdownByTeamId = Object.fromEntries(
        (breakdown || []).map(b => [b.team_id, b])
    );
    const fieldFinal = isPlayoffFieldFinal(tournament);
    const swatchSport = sportForTemplate(tournament?.template);
    return (
        <div className={compact ? "" : ""}>
            {/* iter87.25 — Outer card + "Your teams/clubs" header removed;
                Takeover header already surfaces the "MY TEAMS" title
                with a matching icon. Only the grid remains. */}
            <BentoTileGrid className={`grid gap-2 ${compact ? "grid-cols-2 sm:grid-cols-3 lg:grid-cols-6" : "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4"}`}>
                {teams.map(t => {
                    const bd = breakdownByTeamId[t.id];
                    const isPowerPick = bd?.is_power_pick;
                    const powerBonus = bd?.advancement_power_bonus || 0;
                    // iter87.261 — a drafted team can slide out of the
                    // playoff field after the pick was made (see
                    // mlb_engine.py/nba_engine.py `missed_playoffs`).
                    // Distinct from `eliminated` (which only fires once
                    // a team actually loses a real bracket series).
                    const missedPlayoffs = !!t.missed_playoffs && !t.eliminated;

                    // Tile framing — hairline Bento pill, soft glow for power picks,
                    // subtle dim + reduced saturation for eliminated teams.
                    const tileBase = "relative rounded-lg border transition-colors";
                    const tilePad = compact ? "p-2.5" : "p-3";
                    const tileTone = isPowerPick
                        ? "border-volt/60 bg-volt/[0.06] shadow-[0_0_0_1px_rgba(212,246,68,0.15),0_0_24px_-6px_rgba(212,246,68,0.35)]"
                        : missedPlayoffs
                            ? (fieldFinal ? "border-amber-500/30 bg-amber-500/[0.04] opacity-70" : "border-amber-500/40 bg-amber-500/[0.05]")
                            : t.eliminated
                                ? "border-white/[0.06] bg-white/[0.02] opacity-60"
                                : "border-white/[0.08] bg-[#141414] hover:bg-[#181818]";

                    const stageLine = missedPlayoffs
                        ? (fieldFinal ? "Missed the field" : "Outside the field (not final)")
                        : isNfl
                            ? `${t.group_wins || 0}-${t.group_losses || 0}${t.group_draws ? `-${t.group_draws}` : ""}`
                            : isSeason
                                ? `${t.group_wins || 0}W · ${t.group_draws || 0}D · ${t.group_losses || 0}L`
                                : STAGE_LABELS[t.stage_reached];

                    return (
                        <motion.div
                            key={t.id}
                            data-testid={`my-team-${t.id}`}
                            variants={{
                                hidden: { opacity: 0, y: 10, scale: 0.97 },
                                visible: { opacity: 1, y: 0, scale: 1 },
                            }}
                            whileHover={{ y: -2, scale: 1.01 }}
                            transition={{ type: "spring", stiffness: 380, damping: 32 }}
                            className={`${tileBase} ${tilePad} ${tileTone}`}
                        >
                            {isPowerPick && (
                                <div
                                    className={`absolute -top-2 right-2 inline-flex items-center gap-1 rounded-full bg-sky-400 text-black px-1.5 py-0.5 text-[8px] font-black uppercase tracking-widest`}
                                    data-testid={`power-pick-badge-${t.id}`}
                                >
                                    <Zap className="w-2.5 h-2.5" strokeWidth={3} /> PWR
                                </div>
                            )}

                            {/* Head: flag chip + status badge (skull / trophy) */}
            <div className="flex items-center justify-between mb-1.5">
                                {nationalFlag(t.flag) ? (
                                    <div className={`inline-flex items-center justify-center rounded-full border border-white/10 bg-white/[0.04] ${compact ? "w-7 h-7 text-base" : "w-8 h-8 text-lg"}`}>
                                        {nationalFlag(t.flag)}
                                    </div>
                                ) : (
                                    <TeamSwatch
                                        name={t.name}
                                        sport={swatchSport}
                                        size={compact ? "w-7 h-7 text-[10px]" : "w-8 h-8 text-[11px]"}
                                        testId={`my-team-initials-${t.id}`}
                                    />
                                )}
                                {t.eliminated && (
                                    <span className={`inline-flex items-center justify-center rounded-full border border-heat/40 bg-heat/10 text-heat ${compact ? "w-5 h-5" : "w-6 h-6"}`} title="Eliminated">
                                        <Skull className={compact ? "w-3 h-3" : "w-3 h-3"} />
                                    </span>
                                )}
                                {missedPlayoffs && (
                                    <span
                                        data-testid={`missed-playoffs-badge-${t.id}`}
                                        className={`inline-flex items-center justify-center rounded-full border border-amber-500/40 bg-amber-500/10 text-amber-400 ${compact ? "w-5 h-5" : "w-6 h-6"}`}
                                        title={fieldFinal ? "Missed the playoff field" : "Currently outside the field — seeding isn't final yet"}
                                    >
                                        <AlertTriangle className={compact ? "w-3 h-3" : "w-3 h-3"} />
                                    </span>
                                )}
                            </div>

                            <p className={`font-bold truncate ${t.eliminated || (missedPlayoffs && fieldFinal) ? "text-zinc-400 line-through" : "text-white"} ${compact ? "text-xs" : "text-[13px]"}`}>{t.name}</p>
                            <p className={`mt-0.5 ${missedPlayoffs ? "text-amber-400 font-semibold" : "text-zinc-500"} ${compact ? "text-[10px]" : "text-[10px]"}`}>
                                {stageLine}
                            </p>

                            {bd && (
                                <p className={`font-mono font-black text-cyber mt-1.5 ${compact ? "text-sm" : "text-base"}`}>
                                    {bd.total}
                                    <span className="text-[9px] uppercase tracking-widest text-zinc-500 font-bold ml-1">pts</span>
                                    {powerBonus > 0 && (
                                        <span className={`text-sky-400 ml-1 font-bold ${compact ? "text-[9px]" : "text-[10px]"}`} title={`+${powerBonus} from Power Pick multiplier`}>
                                            +{powerBonus}⚡
                                        </span>
                                    )}
                                </p>
                            )}
                        </motion.div>
                    );
                })}
            </BentoTileGrid>
        </div>
    );
}
